import { useState, useEffect } from "react";
import { cloneDeep } from "lodash";
import CustomButtonLayout from "../layout/CustomButtonLayout";
import MachineStateCodeUpdateDialog from "../dialogTypes/MachineStateCodeUpdateDialog";

/**
 * @COMPONENT_NAME MachineStateCodeUpdateButton
 * @DESCRIPTION 그리드에서 선택한 설비 데이터에서 설비 상태 변경을 할 수 있는 버튼입니다.
 *              커스텀 dialog에 필요한 데이터를 props로 전달합니다.
 *              
 * @PROPS
 *    - {string} id - 컴포넌트 식별자
 *    - {object} schema - 렌더링할 Form 스키마
 *    - {object} grid - 데이터 그리드 객체 (선택된 항목 정보 활용)
 *    - {object} refresh - 데이터 갱신을 위한 객체, `refresh.refresh()` 호출
 *    - {Function} onSuccess - 작업 성공 시 호출되는 콜백
 *    - {Function} modifyFormDataCallback - Form 제출 전에 데이터를 수정하는 콜백
 *    - {Function} modifySchemaCallback - 스키마를 동적으로 수정하는 콜백
 *    - {Function} checkFormValidationCallback - 유효성 검사를 위한 콜백
 *    - {Function} customizeErrorMsgCallback - 에러 메시지를 커스터마이징하는 콜백
 *    - {Function} doNotUseKeyAsUrlWhenUpdate - Update 시 key값을 url로 사용하지 않게끔 하는 콜백
 *
 * @PASSTHROUGH_PROPS
 *    - {string} id - 컴포넌트 식별자
 *    - {object} schema - 렌더링할 Form 스키마
 *    - {Function} modifyUrlCallback - 요청 URL을 커스터마이징하는 콜백
 *    - {Function} modifyFormDataCallback - Form 제출 전에 데이터를 수정하는 콜백
 *    - {Function} checkFormValidationCallback - 유효성 검사를 위한 콜백
 *    - {Function} customizeErrorMsgCallback - 에러 메시지를 커스터마이징하는 콜백
 *    - {Function} doNotUseKeyAsUrlWhenUpdate - Update 시 key값을 url로 사용하지 않게끔 하는 콜백
 *
 * @CALLBACKS
 *    - {Function} onUpdateSuccess - Form 제출 성공 시 호출되며, 상위에 알림 등을 전달할 수 있음
 *    - {Function} onClose - open state를 false로 변경
 *    - {Function} onOpen - open state를 true로 변경
 *
 * @EXAMPLES
 * ```jsx

    <MachineStateCodeUpdateDialog
    id={id + "update-dialog"}
    schema={modifiedSchema}
    initialData={formData}
    open={() => setOpen(true)}
    onClose={()=>setOpen(false)}
    onSuccess={msg) => notify(msg)}
    modifyUrlCallback={(url) => setUrl(url)}
    setInitialDataCallback={setInitialDataCallback}
    modifyFormDataCallback={modifyFormDataCallback}
    checkFormValidationCallback={checkFormValidationCallback}
    customizeErrorMsgCallback={customizeErrorMsgCallback}
    doNotUseKeyAsUrlWhenUpdate={doNotUseKeyAsUrlWhenUpdate}                
/>
 * ```
 */

function MachineStateCodeUpdateButton(props) {

    const {
        id,
        schema,
        grid,
        refresh,
        onSuccess,
        modifyFormDataCallback,
        modifySchemaCallback,
        checkFormValidationCallback,
        customizeErrorMsgCallback,
        doNotUseKeyAsUrlWhenUpdate,
        ...others
    } = props;

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState();

    const [modifiedSchema, setModifiedSchema] = useState(schema);

    useEffect(() => {
        setModifiedSchema(schema);

        if (modifySchemaCallback) {
            const newSchema = cloneDeep(schema);

            if (newSchema) {
                modifySchemaCallback(newSchema, { ...grid.selectedItems[0] });

                setModifiedSchema(newSchema);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]);

    const onOpen = () => {
        if (grid) {
            setFormData({ ...grid.selectedItems[0] });
        }
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onUpdateSuccess = (msg) => {
        if (onSuccess) {
            onSuccess(msg);
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    const setInitialDataCallback = (formData) => {
        formData = { ...grid.selectedItems[0], ...formData };
    }

    const modifyUrlCallback = (url) => {
        // 창을 연 뒤 목록이 다시 읽히면 선택이 첫 행으로 바뀐다 — 본문(formData)과 같은, 창을 연 설비로 보낸다
        url = `${url}/${formData?.machineCode}`
        return url;

    }

    return (
        <>
            <CustomButtonLayout id={id + "update-button"} onClick={onOpen} buttonName="buttonName.MachineStateCode.update" {...others} />
            <MachineStateCodeUpdateDialog
                id={id + "update-dialog"}
                schema={modifiedSchema}
                initialData={formData}
                open={open}
                onClose={onClose}
                onSuccess={onUpdateSuccess}
                modifyUrlCallback={modifyUrlCallback}
                setInitialDataCallback={setInitialDataCallback}
                modifyFormDataCallback={modifyFormDataCallback}
                checkFormValidationCallback={checkFormValidationCallback}
                customizeErrorMsgCallback={customizeErrorMsgCallback}
                doNotUseKeyAsUrlWhenUpdate={doNotUseKeyAsUrlWhenUpdate}
            />
        </>
    );
}

export default MachineStateCodeUpdateButton;
